import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { usePlayerTeam, useJoinTeam } from '@/hooks/use-coach';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  Users,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics/posthog';

export default function JoinTeamPage() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: currentTeam, isLoading: teamLoading } = usePlayerTeam();
  const joinTeamMutation = useJoinTeam();

  const [inviteCode, setInviteCode] = useState('');
  const [isJoined, setIsJoined] = useState(false);
  const [joinedTeam, setJoinedTeam] = useState<{ name: string } | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }

    // Coaches can't join teams
    if (!authLoading && user?.accountType === 'coach') {
      toast({
        title: 'Coaches cannot join teams',
        description: 'As a coach, you create teams for athletes to join.',
        variant: 'destructive',
      });
      setLocation('/coach/dashboard');
      return;
    }
  }, [isAuthenticated, authLoading, user, setLocation, toast]);

  // If already on a team, show that info
  useEffect(() => {
    if (currentTeam?.hasTeam) {
      // Already on a team, could redirect or show message
    }
  }, [currentTeam]);

  if (authLoading || teamLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType === 'coach') {
    return null;
  }

  const handleJoinTeam = async () => {
    if (!inviteCode.trim()) {
      toast({
        title: 'Invite code required',
        description: 'Please enter the invite code from your coach.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await joinTeamMutation.mutateAsync(inviteCode.trim().toUpperCase());
      setIsJoined(true);
      setJoinedTeam({ name: result.team.name });
      trackEvent('team_joined', {
        team_name: result.team.name,
        invite_code: inviteCode.trim().toUpperCase(),
      });
    } catch (error: any) {
      try {
        trackEvent('team_join_failed', {
          invite_code: inviteCode.trim().toUpperCase(),
          error_message: error?.message || 'unknown',
        });
      } catch {}
    }
  };

  // Already on a team
  if (currentTeam?.hasTeam && !isJoined) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="max-w-lg mx-auto">
            <Link href="/">
              <Button variant="ghost" className="mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>

            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Already on a Team</CardTitle>
                <CardDescription>
                  You're currently a member of <strong>{currentTeam.team?.name}</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="glass-card dark:glass-card-dark rounded-2xl p-4 border-l-4 border-[var(--apple-blue)]">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[var(--apple-blue)] mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-200">
                        To join a different team, you'll need to leave your current team first.
                        Go to Settings to manage your team membership.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link href="/settings" className="flex-1">
                    <Button variant="outline" className="w-full">
                      Go to Settings
                    </Button>
                  </Link>
                  <Link href="/" className="flex-1">
                    <Button className="w-full btn-primary">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  // Success state
  if (isJoined && joinedTeam) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="max-w-lg mx-auto">
            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Welcome to the Team!</CardTitle>
                <CardDescription>
                  You've successfully joined <strong>{joinedTeam.name}</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="glass-card dark:glass-card-dark rounded-2xl p-4 border-l-4 border-[var(--apple-green)]">
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Your coach can now see your training plans and provide feedback on your progress.
                    You'll also have access to team credits for generating training plans.
                  </p>
                </div>

                <Link href="/">
                  <Button className="w-full btn-primary py-6 text-lg rounded-2xl">
                    Start Training
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  // Join form
  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-lg mx-auto">
          <Link href="/">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>

          <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Join a Team</CardTitle>
              <CardDescription>
                Enter the invite code from your coach to join their team.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite Code</Label>
                <Input
                  id="inviteCode"
                  placeholder="Enter 8-character code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="text-center text-2xl tracking-widest font-mono py-6"
                  maxLength={8}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inviteCode.trim()) {
                      handleJoinTeam();
                    }
                  }}
                />
              </div>

              <Button
                className="w-full btn-primary py-6 text-lg rounded-2xl"
                onClick={handleJoinTeam}
                disabled={!inviteCode.trim() || joinTeamMutation.isPending}
              >
                {joinTeamMutation.isPending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Joining...
                  </>
                ) : (
                  'Join Team'
                )}
              </Button>

              <div className="glass-card dark:glass-card-dark rounded-2xl p-4">
                <h4 className="font-medium mb-2">What happens when you join?</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[var(--apple-green)] mt-0.5 shrink-0" />
                    <span>Use team credits for training plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[var(--apple-green)] mt-0.5 shrink-0" />
                    <span>Your coach can view your progress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[var(--apple-green)] mt-0.5 shrink-0" />
                    <span>Get personalized feedback from your coach</span>
                  </li>
                
                </ul>
              </div>

              {user?.planTier && user.planTier !== 'free' && (
                <div className="glass-card dark:glass-card-dark rounded-2xl p-4 border-l-4 border-[var(--apple-orange)]">
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    <strong>Note:</strong> Only free-tier players can join teams
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
